import { forwardRef } from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Link, type LinkProps } from 'react-router-dom';
import classNames from '@/utils/classnames';
import { Spinner } from '../Spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-white hover:bg-brand-hover active:bg-brand-active',
        destructive: 'bg-danger-solid text-white hover:bg-danger-solid-hover',
        secondary: 'border border-line bg-surface-3 text-fg-secondary hover:bg-surface-4 hover:text-fg',
        ghost: 'bg-transparent text-fg-tertiary hover:bg-surface-3 hover:text-fg',
        icon: 'bg-transparent text-fg-tertiary hover:bg-surface-3 hover:text-fg',
      },
      size: {
        7: 'h-7 text-xs rounded-sm',
        8: 'h-8 text-xs rounded-md',
        9: 'h-9 text-sm rounded-lg',
      },
      shape: {
        rectangle: '',
        square: 'p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      { shape: 'rectangle', size: 7 as const, className: 'px-2.5' },
      { shape: 'rectangle', size: 8 as const, className: 'px-3' },
      { shape: 'rectangle', size: 9 as const, className: 'px-4' },
      { shape: 'square', size: 7 as const, className: 'w-7' },
      { shape: 'square', size: 8 as const, className: 'w-8' },
      { shape: 'square', size: 9 as const, className: 'w-9' },
    ],
    defaultVariants: {
      variant: 'secondary',
      size: 8 as const,
      shape: 'rectangle',
      fullWidth: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>, ButtonVariantProps {
  isLoading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, shape, fullWidth, isLoading, iconLeft, iconRight, disabled, children, ...props }, ref) => {
    return (
      <button
        className={buttonVariants({ variant, size, shape, fullWidth })}
        disabled={disabled || isLoading}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <Spinner size={4} />
        ) : (
          <>
            {iconLeft}
            {children}
            {iconRight}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

interface LinkButtonProps extends ButtonVariantProps {
  to: LinkProps['to'];
  isExternal?: boolean;
  target?: string;
  rel?: string;
  children?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  title?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export const LinkButton: React.FC<LinkButtonProps> = ({
  variant,
  size,
  shape,
  fullWidth,
  to,
  isExternal,
  target,
  rel,
  children,
  iconLeft,
  iconRight,
  ...props
}) => {
  const classes = buttonVariants({ variant, size, shape, fullWidth });

  if (isExternal) {
    return (
      <a
        href={to as string}
        target={target}
        rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)}
        className={classes}
        {...props}
      >
        {iconLeft}
        {children}
        {iconRight}
      </a>
    );
  }

  return (
    <Link to={to} target={target} rel={rel} className={classes} {...props}>
      {iconLeft}
      {children}
      {iconRight}
    </Link>
  );
};
LinkButton.displayName = 'LinkButton';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  size?: ButtonVariantProps['size'];
  /** Extra classes merged onto the icon-button recipe (e.g. an active-state color). */
  className?: string;
  children: React.ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 8 as const, className, children, ...props }, ref) => {
    return (
      <button
        className={classNames(buttonVariants({ variant: 'icon', shape: 'square', size }), className)}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';
